// Import necessary components and types from react-hook-form library
// Control: Manages form state and methods
// Controller: Connects form inputs to react-hook-form
// FieldValues: Type for form values (key-value pairs)
// Path: Type for safely accessing nested form fields
import { Control, Controller, FieldValues, Path } from "react-hook-form";
// Import Input component from Chakra UI library for styling
import { Input } from "@chakra-ui/react";

// Define props interface for the component using generics
// TFieldValues extends FieldValues: Makes the component work with any form structure
type EmailInputControlledProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>; // Field name in the form (type-safe)
  control: Control<TFieldValues>; // Form control object from useForm hook
  rules?: any; // Optional validation rules
  invalidMessage?: string; // Custom error message for invalid email
  placeholder?: string; // Placeholder text for the input
};

// Define the component as a generic function component
// This allows it to work with any form structure while maintaining type safety
export const EmailInputControlled = <TFieldValues extends FieldValues>({
  name,
  control,
  rules,
  invalidMessage,
  placeholder,
  ...props // Collect any additional props to pass to the Input component
}: EmailInputControlledProps<TFieldValues>) => {
  // Define regex pattern for email validation
  // Validates common email format: username@domain.tld
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  return (
    // Controller connects the input to react-hook-form
    <Controller
      name={name} // Field name in the form
      control={control} // Form control object
      rules={{
        ...rules, // Spread any additional validation rules
        pattern: {
          // Add email pattern validation
          value: EMAIL_REGEX,
          message: invalidMessage, // Custom error message
        },
      }}
      // Render function provides field props and state
      render={({ field, fieldState }: { field: any; fieldState: any }) => (
        // Render Chakra UI Input component
        <Input
          {...field} // Spread field props (value, onChange, onBlur, etc.)
          {...props} // Spread additional props passed to the component
          placeholder={placeholder}
          autoComplete="email" // Browser hint for autofill
          isInvalid={!!fieldState.error} // Show error styling if validation fails
        />
      )}
    />
  );
};
